"use client";

import React, { useState, useCallback, useMemo, useEffect } from "react";
import Cookies from "js-cookie";
import Link from "next/link";
import Image from "next/image";
import { useSelector, useDispatch } from "react-redux";
import { useRouter, usePathname, useSearchParams } from "next/navigation";
import "./FashionHeader.scss";

// Icons
import { AiOutlineSearch, AiOutlineUser, AiOutlineHeart } from "react-icons/ai";
import { BsHandbag } from "react-icons/bs";
import { FaWhatsapp } from "react-icons/fa";

// Import existing components
import SearchBar from "../../SearchBar/SearchBar";
import FashionCartDrawer from "@/components/CartPage/Modules/FashionCartDrawer/FashionCartDrawer";
import WishlistDrop from "@/components/CheckoutPage/Modules/WishListModule/Modules/WishlistDropdown/WishlistDrop";
import LoginDropdown from "../Navigation/LoginDropdown/LoginDropdown";
import AccountStatus from "@/functions/Cookies/Cookies";
import { updateCartCount } from "@/redux/Slices/cartSlice";

import FashionHeaderToggleButtons from "./FashionHeaderToggleButtons";
import FashionHeaderMegaMenu from "./FashionHeaderMegaMenu";
import FashionMobileHeader from "./FashionMobileHeader";

// Import logo
import logo from "@/assets/alfafashionimages/alfafashionlogo.svg";
import LoginUserIcon from "@/assets/Icons/LoginUserIcon.svg";
import FashionHeaderCountryDropdown from "../Navigation/AlfaHeaderCountryDropdown/AlfaHeaderCountryDropdown";

const NAVIGATION_ITEMS = [
  { label: "NEW ARRIVALS", href: "/fashion/new-arrivals" },
  { label: "READY TO DELIVER", href: "/fashion/ready-to-deliver" },
  { label: "SHOES", href: "/fashion/shoes" },
  { label: "BEAUTY", href: "/fashion/beauty" },
  { label: "DESIGNERS", href: "/fashion/designers" },
  { label: "BEST SELLER", href: "/fashion/best-seller" },
  { label: "SALE", href: "/fashion/sale", isSale: true },
];

const FashionHeader = ({ navigationData = [], userAgent }) => {
  const searchParams = useSearchParams();
  const isV1 = searchParams?.get('q') === 'v1';
  const { loggedIn } = AccountStatus();
  const [countryIdFromCookies] = useState(
    () => Cookies.get("fashionCurrencyId") || 23
  );
  const [showSearch, setShowSearch] = useState(false);
  const dispatch = useDispatch();

  const cartId = Cookies.get("cartId");
  const fashionCartId = Cookies.get("fashion_cart_id");

  const cartCountTotalValue = useSelector((state) => state.cartSlice.cartCountTotal);

  // Fetch cart count when component mounts or when fashion cart ID changes
  useEffect(() => {
    const fetchCartCount = async () => {
      if (fashionCartId) {
        try {
          const storeId = Cookies.get("fashionCurrencyId") || "23";
          const response = await fetch(
            `${process.env.NEXT_PUBLIC_BACKEND}/order/view_cart?storefront=${storeId}`,
            {
              method: "GET",
              headers: {
                "Content-Type": "application/json",
                cartid: fashionCartId,
              },
            }
          );

          if (response.ok) {
            const cartData = await response.json();
            dispatch(updateCartCount(cartData?.line_items?.length || 0));
          }
        } catch (error) {
          console.error("Error fetching cart count:", error);
        }
      } else {
        dispatch(updateCartCount(0));
      }
    };

    fetchCartCount();
  }, [fashionCartId, dispatch]);

  // ========================================================================
  // Memoized Path Checks
  // ========================================================================
  const router = useRouter();
  const pathname = usePathname();

  const isWomenActive = useMemo(() => {
    return pathname === "/fashion" || pathname.startsWith("/fashion/") && !pathname.startsWith("/fashion/men");
  }, [pathname]);

  const isMenActive = useMemo(() => {
    return pathname.startsWith("/fashion/men");
  }, [pathname]);

  const showCountryDropdown = !pathname.includes("/cart");

  const handleSearchToggle = useCallback(() => {
    setShowSearch((prev) => !prev);
  }, []);

  const renderTopBar = () => {
    if (showSearch || isV1) return null;
    return (
      <div className="fashion-header__top-bar">
        <div className="fashion-header__top-bar-content">
          <div className="fashion-header__top-bar-left">
            {/* <Link
              href="/fashion"
              className={`fashion-header__top-link ${isWomenActive ? "fashion-header__top-link--active" : ""}`}
            >
              WOMEN
            </Link>
            <Link
              href="/fashion/men"
              className={`fashion-header__top-link ${isMenActive ? "fashion-header__top-link--active" : ""}`}
            >
              MEN
            </Link> */}
          </div>
          <div className="fashion-header__top-bar-right">
            <span className="fashion-header__promo-text">
              Free Delivery on All Prepaid Orders!
            </span>
          </div>
        </div>
      </div>
    );
  };

  const renderLogoSection = () => (
    <div className="fashion-header__logo-section">
      <div className="fashion-header__logo-row-center">
        <Link href="/fashion">
          <Image
            src={logo}
            height={40}
            width={40}
            alt="Alfa Fashion Logo"
            className="fashion-header__logo"
            priority
          />
        </Link>
      </div>

      {showSearch && (
        <div className="fashion-header__search-bar-wrapper">
          {countryIdFromCookies && (
            <SearchBar
              userAgent={userAgent}
              ShowMegaSearch={showSearch}
              setShowMegaSearch={setShowSearch}
              countryIdFromCookies={countryIdFromCookies}
              isFashion={true}
            />
          )}
        </div>
      )}

      <div className="fashion-header__logo-row-right">
        {!showSearch && (
          <div className="fashion-header__toggle-buttons-wrapper">
            <FashionHeaderToggleButtons />
          </div>
        )}

        <div className="fashion-header__icons">
          {/* Search Icon */}
          {!showSearch && (
            <div
              className="fashion-header__icon"
              onClick={handleSearchToggle}
              role="button"
              tabIndex={0}
              aria-label="Toggle search"
            >
              <AiOutlineSearch style={{ fontSize: "20px" }} />
            </div>
          )}

          {!showSearch && (
            <>
              {/* Wishlist */}
              <div className="fashion-header__icon">
                <WishlistDrop
                  customIcon={<AiOutlineHeart style={{ fontSize: "20px" }} />}
                  useFashionCookies
                />
              </div>

              {/* Cart */}
              <div className="fashion-header__icon">
                {cartCountTotalValue > 0 && (
                  <div className="fashion-header__cart-count">
                    {cartCountTotalValue}
                  </div>
                )}
                <FashionCartDrawer
                  customIcon={<BsHandbag style={{ fontSize: "18px" }} />}
                />
              </div>

              {/* Account
              <div className="fashion-header__icon">
                <LoginDropdown
                  loggedIn={loggedIn}
                  isMobile={userAgent?.isMobile}
                  customIcon={
                    <Image
                      src={LoginUserIcon}
                      height={20}
                      width={20}
                      alt="User"
                    />
                  }
                />
              </div> */}

              {/* WhatsApp */}
              <a
                href="https://api.whatsapp.com/send?phone=923000555746&text=Hi!%20I%20have%20a%20question"
                target="_blank"
                rel="noopener noreferrer"
                className="fashion-header__icon fashion-header__whatsapp-link"
                aria-label="Contact us on WhatsApp"
              >
                <FaWhatsapp style={{ fontSize: "20px", color: "#000" }} />
              </a>
            </>
          )}
        </div>
      </div>
    </div>
  );

  const renderNavigation = () => {
    if (showSearch || isV1) return null;
    // Debug logging
    console.log("🔍 Navigation render:", {
      navigationDataLength: navigationData?.length,
      navigationData,
    });

    if (!navigationData || navigationData.length === 0) {
      return (
        <div className="fashion-header__navigation">
          <div className="fashion-header__nav-inner">
            {showCountryDropdown && (
              <div className="fashion-header__nav-left">
                <FashionHeaderCountryDropdown />
              </div>
            )}
            <nav className="fashion-header__nav-menu">
              {NAVIGATION_ITEMS.map((item) => (
                <Link
                  key={item.href}
                  href={item.href}
                  className={`fashion-header__nav-link ${
                    item.isSale ? "fashion-header__nav-link--sale" : ""
                  }`}
                >
                  {item.label}
                </Link>
              ))}
            </nav>
          </div>
        </div>
      );
    }

    return (
      <div className="fashion-header__navigation">
        <div className="fashion-header__nav-inner">
          {showCountryDropdown && (
            <div className="fashion-header__nav-left">
              <FashionHeaderCountryDropdown />
            </div>
          )}
          <FashionHeaderMegaMenu
            navigationData={navigationData}
            userAgent={userAgent}
          />
        </div>
      </div>
    );
  };
  // mobile header
  if (userAgent?.isMobile) {
    return (
      <FashionMobileHeader
        navigationData={navigationData}
        userAgent={userAgent}
      />
    );
  }

  // Desktop header
  return (
    <div
      className={`fashion-header ${showSearch ? "fashion-header--search-active" : ""}`}
    >
      {renderTopBar()}
      {renderLogoSection()}
      {renderNavigation()}
    </div>
  );
};

export default FashionHeader;
